// ============================================================================
// pacsImage.js — 前端 PACS 影像調閱 API 封裝（函數式、無狀態）
//
// 對應後端 routes/pacs-image.js（client / nginx 前綴 /api）：
//   findImageSets     GET  /api/pacs-image/image-sets
//   retrieveImageSet  POST /api/pacs-image/image-sets/:imageSetId/retrieve
//   listInstances     GET  /api/pacs-image/image-sets/:imageSetId/instances
//   instanceUrl       GET  /api/pacs-image/image-sets/:imageSetId/instances/:file
//                          （dwv 直接吃此 URL）
//
// ★ imageSetId 是「後端發的不透明識別碼」，前端一律原樣帶回，**不得解析、不得
//   自行組裝**。它在不同 PACS 廠商底下是不同的東西（dcm4chee = StudyInstanceUID、
//   醫位 = 檢查單號），前端不需要也不應該知道——這正是後端抽象的意義（見
//   server/service/pacs/PacsImageSource.js 與 docs/adr/0006）。
//
// ★ 前端絕不直接連 PACS / 醫位：所有取像一律經 EVAS server 代理。醫位的 HIS
//   帳密只存在 server 端 .env，不進 bundle、不進瀏覽器（驗收 A4/A5）。
//
// 設計：純 fetch 封裝，回傳 plain object；錯誤往上拋由元件處理顯示。
// ============================================================================

const API_BASE = '/api/pacs-image'

/**
 * 查詢可取像的影像群組
 * @param {{ patientId?: string, accessionNumber?: string, studyDateRange?: string, modality?: string }} params
 * @returns {Promise<Array<Object>>} ImageSet 陣列（除 imageSetId 外欄位皆可能不存在）
 */
export async function findImageSets(params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString()
  const res = await fetch(`${API_BASE}/image-sets?${qs}`)
  if (!res.ok) {
    const err = await safeJson(res)
    throw new Error(err.message || `PACS 查詢失敗（${res.status}）`)
  }
  const data = await res.json()
  return data.imageSets || []
}

/**
 * 觸發取像（後端走 DIMSE C-MOVE/C-GET 或醫位 WADO 的 N 次 HTTP GET 落地）
 * @param {string} imageSetId
 * @returns {Promise<{ imageSetId: string, instances: string[], failedCount: number }>}
 */
export async function retrieveImageSet(imageSetId) {
  const res = await fetch(
    `${API_BASE}/image-sets/${encodeURIComponent(imageSetId)}/retrieve`,
    { method: 'POST' }
  )
  if (!res.ok) {
    const err = await safeJson(res)
    throw new Error(err.message || `PACS 取像失敗（${res.status}）`)
  }
  return res.json()
}

/**
 * 列出某影像群組已落地（快取）的 instance 檔名
 * @param {string} imageSetId
 * @returns {Promise<string[]>}
 */
export async function listInstances(imageSetId) {
  const res = await fetch(
    `${API_BASE}/image-sets/${encodeURIComponent(imageSetId)}/instances`
  )
  if (!res.ok) throw new Error(`列出影像失敗（${res.status}）`)
  const data = await res.json()
  return data.instances || []
}

/**
 * 組出單張 .dcm 的 URL（交給 dwv 的 loadURLs 直接載入原生 DICOM）
 * @param {string} imageSetId
 * @param {string} instanceFile
 * @returns {string}
 */
export function instanceUrl(imageSetId, instanceFile) {
  return `${API_BASE}/image-sets/${encodeURIComponent(
    imageSetId
  )}/instances/${encodeURIComponent(instanceFile)}`
}

async function safeJson(res) {
  try {
    return await res.json()
  } catch {
    return {}
  }
}

export default { findImageSets, retrieveImageSet, listInstances, instanceUrl }
